/**
 * InvestigationList Component - Grid de Cards Responsivo
 * Baseado em best practices 2025: CSS Grid auto-fill, card design patterns
 *
 * Padrão:
 * - CSS Grid com auto-fill (adapta automaticamente)
 * - Mobile: 1 coluna
 * - Tablet: 2 colunas
 * - Desktop: 3-4 colunas
 * - Empty state quando não houver investigações
 * - Loading state com skeleton
 *
 * Referências:
 * - https://dopebase.com/blog/creating-responsive-card-grid-layout-css-grid-react
 * - https://github.com/react-grid-layout/react-grid-layout
 * - https://demo.mobiscroll.com/react/cards/grid-layout
 */

import { formatRelativeTime, formatNumber } from '../../utils/formatters';
import type { Investigation } from '../../types/investigation';
import { Button } from '../ui/Button';
import {
  EyeIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface InvestigationListProps {
  investigations: Investigation[];
  loading?: boolean;
  onSelect?: (investigation: Investigation) => void;
  onDelete?: (investigationId: string) => void;
}

/**
 * Lista de investigações em grid responsivo
 *
 * Features:
 * - ✅ CSS Grid auto-fill responsivo
 * - ✅ Card com preview de dados
 * - ✅ Badges de status (aviso de profundidade)
 * - ✅ Ações: Ver, Deletar
 * - ✅ Empty state
 * - ✅ Loading skeleton
 * - ✅ Mobile-friendly (1 col → 4 cols)
 *
 * @example
 * const { investigations, loading, deleteInvestigation } = useInvestigation();
 *
 * <InvestigationList
 *   investigations={investigations}
 *   loading={loading}
 *   onSelect={(inv) => navigate(`/investigation/${inv.id}`)}
 *   onDelete={(id) => deleteInvestigation(id)}
 * />
 */
export function InvestigationList({
  investigations,
  loading = false,
  onSelect,
  onDelete,
}: InvestigationListProps) {

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (investigations.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
      {investigations.map((investigation) => (
        <InvestigationCard
          key={investigation.investigacao_id || investigation.id}
          investigation={investigation}
          onSelect={onSelect}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

/**
 * Card individual de investigação
 */
interface InvestigationCardProps {
  investigation: Investigation;
  onSelect?: (investigation: Investigation) => void;
  onDelete?: (id: string) => void;
}

function InvestigationCard({ investigation, onSelect, onDelete }: InvestigationCardProps) {
  const id = investigation.investigacao_id || investigation.id || '';
  const { entradaInicial, estatisticas, dataInicio, aviso } = investigation;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Evita trigger do onSelect
    if (confirm('Tem certeza que deseja deletar esta investigação?')) {
      onDelete?.(id);
    }
  };

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
      onClick={() => onSelect?.(investigation)}
    >
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              {getTipoLabel(entradaInicial.tipo)}
            </p>
            <h3 className="text-sm font-semibold text-gray-900 mt-1 truncate" title={entradaInicial.valor}>
              {entradaInicial.valor}
            </h3>
          </div>

          {/* Badge de aviso */}
          {aviso && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0">
              <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
              Limite
            </span>
          )}
        </div>
      </div>

      {/* Body - Estatísticas */}
      <div className="px-4 py-3 space-y-2">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <p className="text-gray-500">Nós</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatNumber(estatisticas.total_nos)}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Arestas</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatNumber(estatisticas.total_arestas)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <p className="text-gray-500">Profundidade</p>
            <p className="text-sm font-medium text-gray-700">
              {estatisticas.profundidade_maxima}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Não expandidos</p>
            <p className="text-sm font-medium text-gray-700">
              {estatisticas.nos_nao_expandidos}
            </p>
          </div>
        </div>

        {/* Timestamp */}
        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Criado {formatRelativeTime(dataInicio)}
          </p>
        </div>
      </div>

      {/* Footer - Ações */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex gap-2">
        <Button
          variant="primary"
          size="sm"
          icon={<EyeIcon className="h-4 w-4" />}
          onClick={() => onSelect?.(investigation)}
          className="flex-1"
        >
          Visualizar
        </Button>
        <Button
          variant="danger"
          size="sm"
          icon={<TrashIcon className="h-4 w-4" />}
          onClick={handleDelete}
          className="flex-shrink-0"
          aria-label="Deletar investigação"
        >
          <span className="sr-only">Deletar</span>
        </Button>
      </div>
    </div>
  );
}

/**
 * Empty state quando não há investigações
 */
function EmptyState() {
  return (
    <div className="text-center py-12 px-4">
      <svg
        className="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma investigação</h3>
      <p className="mt-1 text-sm text-gray-500">
        Comece criando uma nova investigação para visualizar o grafo de relacionamentos.
      </p>
    </div>
  );
}

/**
 * Loading skeleton
 */
function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden animate-pulse">
          {/* Header */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="h-3 bg-gray-300 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-300 rounded w-2/3"></div>
          </div>

          {/* Body */}
          <div className="px-4 py-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="h-8 bg-gray-200 rounded"></div>
              <div className="h-8 bg-gray-200 rounded"></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="h-6 bg-gray-200 rounded"></div>
              <div className="h-6 bg-gray-200 rounded"></div>
            </div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
            <div className="h-8 bg-gray-300 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Helper - Label do tipo de entrada
 */
function getTipoLabel(tipo: string): string {
  const labels: Record<string, string> = {
    cnpj: 'CNPJ',
    pessoa: 'Pessoa',
    email: 'Email',
    telefone: 'Telefone',
    'cpf-nome': 'CPF + Nome',
  };
  return labels[tipo] || tipo;
}
